import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Groupper, Header, Icon, Input, Message, Modal, Table, Toast, Toolbar, Field } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { AvailableItemForShipment, EventDate, InventoryType, Location, SetLoading } from '../SiteClasses';
import { SearchEventModal, SearchTypeModal, SearchAvailableItemsModal } from '../components';
import { useBreadcrumbs } from '../hooks';
import { bindClick, bindFormChange, formatFullAddress, formatPlacesTemp } from '../SiteUtil';
import Autocomplete from '../components/Autocomplete';
import classNames from 'classnames';
import moment from 'moment';
import API from '../API';
import Validator from '../Validator';

enum LocationType{
	ADDRESS = 1,
	ESTABLISHMENT = 2,
}
interface RequestForm{
	request_id?: number,
	is_express?: boolean,
	date_return: number,
	box_office_start_date?: number,
	deleted?: boolean,
	rejected?: boolean,
	rejection_reason?: string,
	description?: string,
	approved?: boolean,
	finalized?: boolean,
	shipment_id?: number,
	user_creator_name?: string,
	date_created?: number,
	items: {
		type_id: number,
		quantity: number
		type_name?: string,
		type_icon?: string,
	}[],
	events: EventDate[]
}

enum Modals{
	SEARCH_EVENT = 1,
	SEARCH_TYPE = 2,
	ADD_TYPE = 3,
	CONFIRM_REMOVE_ITEM = 4,
	CONFIRM_REMOVE_EVENT = 5,
	EDIT_INFO = 6,
	APPROVE = 7,
	REJECT = 8,
	CREATE_SHIPMENT = 9,
	SEARCH_SHIPMENT_ITEMS = 10,
	EDIT_ITEM = 11,
	UNAVAILABLE_ITEMS = 12,
	EVENT_CONFLICTS = 13,
	FINALIZE = 14,
}

interface ConflictingEvent extends EventDate{
	request_id?: number,
}

enum TaquillaRequestTypeIds{
	TERMINAL_BANCARIA = 1,
	CARGADOR_LAPTOP = 6,
	IMPRESORA_BOLETOS = 12,
	LAPTOP = 16,
}

var DEFAULT_TAQUILLA_REQUEST_TYPES = [
	TaquillaRequestTypeIds.TERMINAL_BANCARIA,
	TaquillaRequestTypeIds.CARGADOR_LAPTOP,
	TaquillaRequestTypeIds.IMPRESORA_BOLETOS,
	TaquillaRequestTypeIds.LAPTOP,
];

var RequestView = ()=>{
	var { id } = useParams();
	var location = useLocation();
	var navigate = useNavigate();
	var isCreate = id === 'create';
	var isExpressCreate = isCreate && new URLSearchParams(location.search).get('mode') === 'express';
	var { setTitle } = useBreadcrumbs();
	var [loadError, setLoadError] = useState<string>(null);
	var [prompts, setPrompts] = useState<string[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [form, setForm] = useState<RequestForm>(null);
	var [selectedItem, setSelectedItem] = useState<{ type_id: number, quantity: number, type_name?: string, type_icon?: string }>(null);
	var [selectedEvent, setSelectedEvent] = useState<EventDate>(null);
	var [itemQuantity, setItemQuantity] = useState<string>('1');
	var [rejectionReason, setRejectionReason] = useState<string>('');
	var [editInfoForm, setEditInfoForm] = useState<{ date_return: number, box_office_start_date?: number, description: string }>(null);
	var [shipmentItems, setShipmentItems] = useState<AvailableItemForShipment[]>([]);
	var [shipmentLocation, setShipmentLocation] = useState<Location>(null);
	var [shipmentLocationQuery, setShipmentLocationQuery] = useState('');
	var [shipmentDestinationZipcode, setShipmentDestinationZipcode] = useState('');
	var [shipmentDescription, setShipmentDescription] = useState('');
	var [shipmentPrompts, setShipmentPrompts] = useState<string[]>([]);
	var [selectedShipmentTypeId, setSelectedShipmentTypeId] = useState<number>(null);
	var [unavailableTypeIds, setUnavailableTypeIds] = useState<number[]>([]);
	var [conflictingEvents, setConflictingEvents] = useState<ConflictingEvent[]>([]);
	var [loadingDefaultItems, setLoadingDefaultItems] = useState<boolean>(false);
	var [locationType, setLocationType] = useState<LocationType>(LocationType.ADDRESS);
	var isExpressMode = isCreate ? isExpressCreate : !!form?.is_express;

	var loadDefaultTaquillaItems = async()=>{
		setLoadingDefaultItems(true);
		var defaultItems: RequestForm['items'] = [];

		for(var type_id of DEFAULT_TAQUILLA_REQUEST_TYPES){
			try{
				var res = await API.getAvailableItemsForShipment(null, null, type_id, 0, 1);
				var item = res.data?.[0];
				if(res.error || !item) continue;
				defaultItems.push({
					type_id: item.type_id,
					type_name: item.type_name,
					type_icon: item.type_icon,
					quantity: 1,
				});
			}catch{}
		}

		if(defaultItems.length > 0){
			setForm(f => {
				if(!f) return f;
				var existingTypeIds = new Set(f.items.map(i => i.type_id));
				return {
					...f,
					items: [...f.items, ...defaultItems.filter(i => !existingTypeIds.has(i.type_id))]
				};
			});
		}

		setLoadingDefaultItems(false);
	};

	useEffect(()=>{
		if(isCreate){
			setTitle(isExpressCreate ? 'Nueva solicitud express' : 'Nueva solicitud');
			setForm({
				is_express: isExpressCreate,
				date_return: null,
				description: '',
				items: [],
				events: []
			});
			if(!isExpressCreate) loadDefaultTaquillaItems();
		}else{
			API.getRequest(parseInt(id)).then(res=>{
				if(res.error)return setLoadError(res.message);
				setForm({
					request_id: res.data.request_id,
					is_express: !!res.data.is_express,
					date_return: res.data.date_return,
					box_office_start_date: res.data.box_office_start_date,
					description: res.data.description,
					deleted: !!res.data.deleted,
					rejected: !!res.data.rejected,
					rejection_reason: res.data.rejection_reason,
					approved: !!res.data.approved,
					finalized: !!res.data.finalized,
					shipment_id: res.data.shipment_id,
					user_creator_name: res.data.user_creator_name,
					date_created: res.data.date_created,
					items: res.data.items,
					events: res.data.events
				});
				setTitle(`${res.data.is_express ? 'Solicitud express' : 'Solicitud'} #${res.data.request_id}`);
			}).catch(()=>{
				setLoadError('Hubo un error inesperado al cargar la solicitud.');
			});
		}
	}, [id, isCreate, isExpressCreate]);

	var computeReturnDate = (events: EventDate[])=>{
		if(!events || events.length === 0) return null;
		var maxDate = Math.max(...events.map(e => e.date));
		return moment.unix(maxDate).add(3, 'days').startOf('day').unix();
	};

	var getNextReturnDate = (currentDateReturn: number, events: EventDate[])=>{
		return isExpressMode ? currentDateReturn : computeReturnDate(events);
	};

	var closeModal = ()=>{
		setShownModal(null);
		setModalPrompts([]);
		setRejectionReason('');
		setConflictingEvents([]);
	};

	var showEventConflicts = (events: ConflictingEvent[])=>{
		setConflictingEvents(events);
		setShownModal(Modals.EVENT_CONFLICTS);
	};

	var onEventSelected = (event: any, setLoading: SetLoading)=>{
		if(isCreate){
			setForm(f => {
				var already = f.events.find(e => e.date_id === event.date_id);
				if(already){
					Toast.error('Este evento ya fue agregado.');
					setLoading(false);
					return f;
				}
				var newEvents = [...f.events, event];
				var newReturnDate = getNextReturnDate(f.date_return, newEvents);
				setLoading(false);
				return { ...f, events: newEvents, date_return: newReturnDate };
			});
			closeModal();
		}else{
			setLoading(true);
			API.updateRequestEvent(form.request_id, event.date_id, false).then(res=>{
				if(res.error){
					var eventConflict = res.data?.conflicts?.find((c: { date_id: number, request_id?: number }) => c.date_id === event.date_id);
					if(eventConflict){
						return showEventConflicts([{ ...event, request_id: eventConflict.request_id }]);
					}
					return Toast.error(res.message);
				}
				Toast.success('Evento agregado a la solicitud.');

				setForm(f => {
					var newEvents = [...f.events, event];
					var newReturnDate = getNextReturnDate(f.date_return, newEvents);
					return { ...f, events: newEvents, date_return: newReturnDate };
				});
				closeModal();
			}).catch(()=>{
				Toast.error('Hubo un error inesperado agregando el evento.');
			}).finally(()=>{
				setLoading(false);
			});
		}
	};

	var onRemoveEvent = (date_id: number)=>{
		if(isCreate){
			setForm(f => {
				var newEvents = f.events.filter(e => e.date_id !== date_id);
				var newReturnDate = getNextReturnDate(f.date_return, newEvents);
				return { ...f, events: newEvents, date_return: newReturnDate };
			});
		}else{
			var evt = form.events.find(e => e.date_id === date_id);
			setSelectedEvent(evt);
			setShownModal(Modals.CONFIRM_REMOVE_EVENT);
		}
	};

	var confirmRemoveEvent = (setLoading: SetLoading)=>{
		if(!selectedEvent) return;
		setLoading(true);
		API.updateRequestEvent(form.request_id, selectedEvent.date_id, true).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Evento eliminado de la solicitud.');

			setForm(f => {
				var newEvents = f.events.filter(e => e.date_id !== selectedEvent.date_id);
				var newReturnDate = getNextReturnDate(f.date_return, newEvents);
				return { ...f, events: newEvents, date_return: newReturnDate };
			});
			setSelectedEvent(null);
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado eliminando el evento.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onTypeSelected = (type: InventoryType, setLoading: SetLoading)=>{
		setSelectedItem({ type_id: type.type_id, type_name: type.type_name, type_icon: type.type_icon, quantity: 1 });
		setItemQuantity('1');
		setShownModal(Modals.ADD_TYPE);
		setLoading(false);
	};

	var onConfirmAddType = (setLoading: SetLoading)=>{
		if(!selectedItem) return;
		var qty = parseInt(itemQuantity);
		if(isNaN(qty) || qty <= 0)return setModalPrompts(['La cantidad debe ser un número mayor a 0.']);
		if(isCreate){
			setForm(f => {
				var existing = f.items.find(i => i.type_id === selectedItem.type_id);
				if(existing){
					return {
						...f,
						items: f.items.map(i =>
							i.type_id === selectedItem.type_id
								? { ...i, quantity: i.quantity + qty }
								: i
						)
					};
				}
				return {
					...f,
					items: [...f.items, {
						type_id: selectedItem.type_id,
						type_name: selectedItem.type_name,
						type_icon: selectedItem.type_icon,
						quantity: qty,
					}]
				};
			});
			setSelectedItem(null);
			closeModal();
		}else{
			setLoading(true);
			API.updateRequestItem(form.request_id, selectedItem.type_id, qty, false).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Equipo agregado a la solicitud.');

				setForm(f => {
					var existing = f.items.find(i => i.type_id === selectedItem.type_id);
					if(existing){
						return {
							...f,
							items: f.items.map(i =>
								i.type_id === selectedItem.type_id
									? { ...i, quantity: qty }
									: i
							)
						};
					}
					return {
						...f,
						items: [...f.items, {
							type_id: selectedItem.type_id,
							type_name: selectedItem.type_name,
							type_icon: selectedItem.type_icon,
							quantity: qty,
						}]
					};
				});
				setSelectedItem(null);
				closeModal();
			}).catch(()=>{
				setModalPrompts(['Hubo un error inesperado agregando el equipo.']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	};

	var onRemoveItem = (type_id: number)=>{
		if(isCreate){
			setForm(f => ({ ...f, items: f.items.filter(i => i.type_id !== type_id) }));
		}else{
			var itm = form.items.find(i => i.type_id === type_id);
			setSelectedItem(itm);
			setShownModal(Modals.CONFIRM_REMOVE_ITEM);
		}
	};

	var onEditItem = (type_id: number)=>{
		var itm = form.items.find(i => i.type_id === type_id);
		if(!itm) return;
		setSelectedItem(itm);
		setItemQuantity(String(itm.quantity));
		setModalPrompts([]);
		setShownModal(Modals.EDIT_ITEM);
	};

	var confirmEditItem = (setLoading: SetLoading)=>{
		if(!selectedItem) return;
		var qty = parseInt(itemQuantity);
		if(isNaN(qty) || qty <= 0) return setModalPrompts(['La cantidad debe ser un número mayor a 0.']);
		if(isCreate){
			setForm(f => ({
				...f,
				items: f.items.map(i =>
					i.type_id === selectedItem.type_id ? { ...i, quantity: qty } : i
				)
			}));
			setSelectedItem(null);
			closeModal();
		}else{
			setLoading(true);
			API.updateRequestItem(form.request_id, selectedItem.type_id, qty, false).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Cantidad actualizada.');
				setForm(f => ({
					...f,
					items: f.items.map(i =>
						i.type_id === selectedItem.type_id ? { ...i, quantity: qty } : i
					)
				}));
				setSelectedItem(null);
				closeModal();
			}).catch(()=>{
				setModalPrompts(['Hubo un error inesperado actualizando el equipo.']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	};

	var confirmRemoveItem = (setLoading: SetLoading)=>{
		if(!selectedItem) return;
		setLoading(true);
		API.updateRequestItem(form.request_id, selectedItem.type_id, selectedItem.quantity, true).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Equipo eliminado de la solicitud.');
			setForm(f => ({ ...f, items: f.items.filter(i => i.type_id !== selectedItem.type_id) }));
			setSelectedItem(null);
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado eliminando el equipo.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onCreate = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			...(!isExpressCreate ? {
				events: [{
					rule: 'empty', label: 'Eventos'
				}],
				items: [{
					rule: 'empty', label: 'Equipos solicitados'
				}],
				box_office_start_date: [{
					rule: 'empty', label: 'Inicio taquilla'
				}],
			} : {
				date_return: [{
					rule: 'empty', label: 'Fecha retorno'
				}],
				description: [{
					rule: 'empty', label: 'Comentarios'
				}],
			}),
		});
		setPrompts(prompts);
		if(!valid) return;
		setLoading(true);

		(isExpressCreate ? API.createExpressRequest({
			date_return: form.date_return,
			description: form.description?.trim(),
			events: form.events.map(e => e.date_id),
		}) : API.createRequest({
			date_return: form.date_return,
			box_office_start_date: form.box_office_start_date,
			description: form.description,
			items: form.items.map(i => ({ type_id: i.type_id, quantity: i.quantity })),
			events: form.events.map(e => e.date_id),
		})).then(res => {
			if(res.error){
				if(!isExpressCreate && res.data?.types?.length){
					setUnavailableTypeIds(res.data.types);
					setShownModal(Modals.UNAVAILABLE_ITEMS);
				} else if(res.data?.conflicts?.length){
					showEventConflicts(
						form.events.reduce((acc: ConflictingEvent[], e) => {
							var conflict = res.data.conflicts.find((c: { date_id: number, request_id?: number }) => c.date_id === e.date_id);
							if(conflict) acc.push({ ...e, request_id: conflict.request_id });
							return acc;
						}, [])
					);
					setModalPrompts([res.message])
				} else {
					Toast.error(res.message);
				}
				return;
			}
			Toast.success(isExpressCreate ? 'Solicitud express creada correctamente.' : 'Solicitud creada correctamente.');
			navigate(`/requests/${res.data.request_id}`);
		}).catch(() => {
			Toast.error(`Hubo un error inesperado al crear la ${isExpressCreate ? 'solicitud express' : 'solicitud'}.`);
		}).finally(() => {
			setLoading(false);
		});
	};

	var onApprove = (setLoading: SetLoading)=>{
		setLoading(true);
		API.approveRequest(form.request_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Solicitud aprobada.');
			setForm(f => ({ ...f, approved: true }));
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado aprobando la solicitud.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFinalize = (setLoading: SetLoading)=>{
		setLoading(true);
		API.finalizeRequest(form.request_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Solicitud finalizada.');
			setForm(f => ({ ...f, finalized: true }));
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado finalizando la solicitud.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onReject = (setLoading: SetLoading)=>{
		if(!rejectionReason.trim()){
			return setModalPrompts(['Ingresa un motivo de rechazo.']);
		}
		setLoading(true);
		API.rejectRequest(form.request_id, rejectionReason).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Solicitud rechazada.');
			setForm(f => ({ ...f, approved: false, rejected: true, rejection_reason: rejectionReason }));
			setRejectionReason('');
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado rechazando la solicitud.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onEditInfo = (setLoading: SetLoading)=>{
		if(form.is_express && !editInfoForm.description?.trim()) return setModalPrompts(['Los comentarios son requeridos.']);
		if(!editInfoForm.date_return) return setModalPrompts(['La fecha de retorno es requerida.']);
		if(!form.is_express && !editInfoForm.box_office_start_date) return setModalPrompts(['La fecha de inicio taquilla es requerida.']);
		setLoading(true);
		API.editRequestInfo(form.request_id, editInfoForm.date_return, editInfoForm.box_office_start_date, editInfoForm.description).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Información actualizada.');

			setForm(f => ({ ...f, date_return: editInfoForm.date_return, box_office_start_date: f.is_express ? null : editInfoForm.box_office_start_date, description: editInfoForm.description }));
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado actualizando la información.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openEditInfo = ()=>{
		setEditInfoForm({ date_return: form.date_return, box_office_start_date: form.box_office_start_date, description: form.description || '' });
		setShownModal(Modals.EDIT_INFO);
	};

	var openCreateShipment = ()=>{
		setShipmentItems([]);
		setShipmentLocation(null);
		setShipmentLocationQuery('');
		setShipmentDestinationZipcode('');
		setShipmentDescription('');
		setShipmentPrompts([]);
		setShownModal(Modals.CREATE_SHIPMENT);
	};

	var onShipmentPlaceSelected = (loc: any)=>{
		var formatted = formatPlacesTemp(loc);
		setShipmentLocationQuery(formatFullAddress(formatted));
		setShipmentLocation(formatted);
		setShipmentDestinationZipcode(formatted?.zipcode || '');
	};

	var onRemoveShipmentItem = (item_id: number)=>{
		setShipmentItems(prev => prev.filter(i => i.item_id !== item_id));
	};

	var onShipmentItemSelected = (item: AvailableItemForShipment)=>{
		var already = shipmentItems.find(i => i.item_id === item.item_id);
		if(already)return setShipmentItems(prev => prev.filter(i => i.item_id !== item.item_id));

		var requested = form?.items.find(r => r.type_id === item.type_id);
		if(requested){
			var alreadyOfType = shipmentItems.filter(i => i.type_id === item.type_id).length;
			if(alreadyOfType >= requested.quantity)return Toast.error(`Ya se agregaron los ${requested.quantity} equipos solicitados de este tipo.`);
		}
		setShipmentItems(prev => [...prev, item]);
	};

	var openAddShipmentType = (type_id: number)=>{
		setSelectedShipmentTypeId(type_id);
		setShownModal(Modals.SEARCH_SHIPMENT_ITEMS);
	};

	var onCreateShipment = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ shipmentLocation, shipmentItems }, {
			shipmentLocation: [{ rule: 'empty', label: 'Destino del envío' }],
			shipmentItems: [{ rule: 'empty', label: 'Equipos en el envío' }],
		});
		setShipmentPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createShipment({
			request_id: form.request_id,
			destination: shipmentLocation,
			item_ids: shipmentItems.map(i => i.item_id),
			description: shipmentDescription || undefined,
		}).then(res=>{
			if(res.error) return setShipmentPrompts([res.message]);
			Toast.success('Envío creado correctamente.');
			closeModal();
			navigate(`/shipments/${res.data.shipment_id}`);
		}).catch(()=>{
			setShipmentPrompts(['Hubo un error inesperado creando el envío.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFormChange = bindFormChange(form, setForm);
	var onEditInfoChange = editInfoForm ? bindFormChange(editInfoForm, setEditInfoForm) : null;

	if(!form) return <Header loading text='Cargando' />;
	if(loadError) return <Header text='Error' subtext={loadError} />;
	var isExpressRequest = isExpressMode;

	return <div>
		{!isCreate && form.rejected && (
			<Message centered type='error' style={{ maxWidth: 500, margin: 'auto', marginTop: 20, marginBottom: -5 }} header='Solicitud rechazada' text={form.rejection_reason} />
		)}
		<Groupper
			title={isCreate ? (isExpressRequest ? 'Crear solicitud express' : 'Crear solicitud') : (isExpressRequest ? 'Solicitud express' : 'Solicitud')}
			style={{ marginTop: 20 }}
			fitted
			width={600}
			actions={isCreate &&
				<Button text={'Crear'} color='black' onClick={onCreate} />
			}
		>
			{!isCreate && !form.deleted && (
				<Toolbar stretch>
					{!form.approved && !form.rejected ? (
						<Toolbar.Dropdown
							text='Editar'
							iconName='pencil'
							items={[
								{ text: 'Info', iconName: 'circle-info', onClick: openEditInfo },
							]}
						/>
					) : null}
					{!form.approved && !form.rejected ? <Toolbar.Item text='Aprobar' iconName='check' onClick={bindClick(setShownModal, Modals.APPROVE)} /> : null}
					{form.is_express && form.approved && !form.rejected && !form.finalized ? <Toolbar.Item text='Finalizar' iconName='flag-checkered' onClick={bindClick(setShownModal, Modals.FINALIZE)} /> : null}
					{(!form.approved && !form.rejected) || (form.approved && !form.rejected && !form.shipment_id && !form.finalized) ? <Toolbar.Item text='Rechazar' iconName='ban' onClick={bindClick(setShownModal, Modals.REJECT)} /> : null}
					{form.approved && !form.shipment_id && !form.is_express ? <Toolbar.Item text='Generar envío' iconName='truck' onClick={openCreateShipment} /> : null}
				</Toolbar>
			)}
			{isCreate ? <div style={{ padding: 10 }}>
				{isExpressRequest ? (
					<Input
						label='Fecha retorno'
						value={form.date_return}
						calendar={{ date: form.date_return, mode: 'date', format: 'DD/MM/YY' }}
						onChange={onFormChange('date_return')}
						style={{ width: '100%'}}
					/>
				) : (
					<Field amount={2}>
						<Input
							label='Inicio taquilla'
							value={form.box_office_start_date}
							calendar={{ date: form.box_office_start_date, mode: 'date', format: 'DD/MM/YY hh:mm' }}
							onChange={onFormChange('box_office_start_date')}
							style={{ width: '100%'}}
						/>
						<Input
							label='Fecha retorno'
							value={form.date_return ? moment.unix(form.date_return).format('DD/MM/YY') : ''}
							readonly
							style={{ width: '100%'}}
						/>
					</Field>
				)}
				<Input
					label='Comentarios'
					value={form.description}
					textareaRows={8}
					onChange={onFormChange('description')}
					textarea
				/>
			</div> :
				<Table
					details
					striped
					fitted
					data={[
						['ID', form.request_id],
						['Tipo', form.is_express ? 'Solicitud express' : 'Solicitud'],
						['Fecha retorno', form.date_return ? moment.unix(form.date_return).format('DD/MM/YY') : <Icon name='minus' />],
						...(!form.is_express ? [['Inicio taquilla', form.box_office_start_date ? moment.unix(form.box_office_start_date).format('DD/MM/YY') : <Icon name='minus' />]] : []),
						['Creador', form.user_creator_name],
						['Creación', form.date_created ? moment.unix(form.date_created).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
						...form.rejected ? [['Motivo de rechazo', form.rejection_reason]] : [],
						['Estatus', (
							<div className={classNames("fr label", {
								green: form.approved && !form.finalized && !form.deleted && !form.rejected && !form.shipment_id,
								blue: form.approved && !!form.shipment_id && !form.deleted && !form.finalized,
								black: form.finalized && !form.deleted && !form.rejected,
								red: form.deleted || form.rejected,
								yellow: !form.approved && !form.deleted && !form.rejected,
							})}>
								{form.deleted ? 'Eliminado' : form.rejected ? 'Rechazado' : form.finalized ? 'Finalizado' : form.shipment_id ? 'Enviada' : form.approved ? 'Aprobado' : 'Pendiente'}
							</div>
						)],
						...form.shipment_id ? [['Envío', (
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<span>#{form.shipment_id}</span>
								<Button
									icon
									basic
									iconName='up-right-from-square'
									size='tiny'
									onClick={() => navigate(`/shipments/${form.shipment_id}`)}
								/>
							</div>
						)]] : [],
						['Comentarios', form.description || <Icon name='minus' />],
					]}
				/>
			}
			<Table
				title='Eventos'
				striped
				style={{ marginTop: isCreate ? 15 : 0 }}
				headers={['Evento', 'Nombre', 'Fecha', '']}
				collapsingIndexes={[0, 3]}
				fitted
				button={!form.deleted && !form.rejected && !form.approved &&
					<Button text='Agregar' iconName='circle-plus' color='black' onClick={bindClick(setShownModal, Modals.SEARCH_EVENT)}/>
				}
			>
				{form.events.length !== 0 ? (
					form.events.map(e=>(
						<Table.Row
							collapsingIndexes={[0, 2, 3]}
							key={e.date_id}
							data={[
								e.date_id,
								e.event_name, 
								e.date ? moment.unix(e.date).format('DD/MMM/YY') : <Icon name='minus' />,
								<Button icon basic iconName='eye' size='small' as={Link} to={`/events/${e.date_id}`}  target='_blank'/>,
							]}
						/>
					))
				) : (
				<Table.Cell row className='empty' colSpan={8}>
					{isExpressRequest ? 'Sin eventos relacionados' : 'Selecciona un evento'}
				</Table.Cell>
				)}
			</Table>
			{!isExpressRequest && (
				<Table
					title='Equipos'
					striped
					style={{ marginTop: 0 }}
					headers={['', 'Tipo', 'Cantidad', '']}
					centeredIndexes={[2]}
					fitted
					collapsingIndexes={[0, 2, 3]}
					button={!form.deleted && !form.rejected && !form.approved &&
						<Button text='Agregar' iconName='circle-plus' color='black' onClick={bindClick(setShownModal, Modals.SEARCH_TYPE)} disabled={loadingDefaultItems}/>
					}
				>
					{loadingDefaultItems ? (
						<Table.Cell row className='empty' colSpan={8}>
							<Header loading text='Precargando equipos' />
						</Table.Cell>
					) : form.items.length !== 0 ? (
						form.items.map(e=>(
							<Table.Row
								key={e.type_id}
								centeredIndexes={[2]}
								data={[
									<Icon name={e.type_icon as any}/>,
									e.type_name,
									e.quantity,
									!form.deleted && !form.rejected && !form.approved ? (
										<Dropdown direction='left' icon={<Icon name='ellipsis' style={{ margin: 0 }} />} className='icon'>
											<Dropdown.Menu direction='left'>
												{isCreate && (
													<Dropdown.Item onClick={() => onEditItem(e.type_id)}>
														<Icon name='pencil' style={{ marginRight: 3 }} /> Editar
													</Dropdown.Item>
												)}
												<Dropdown.Item onClick={() => onRemoveItem(e.type_id)}>
													<Icon name='trash' style={{ marginRight: 3 }} /> Eliminar
												</Dropdown.Item>
											</Dropdown.Menu>
										</Dropdown>
									) : null
								]}
							/>
						))
					) : (
					<Table.Cell row className='empty' colSpan={8}>
						Selecciona un equipo
					</Table.Cell>
					)}
				</Table>
			)}
			<Message type='error' list={prompts} style={{ margin: 15  }} />
		</Groupper>
		<SearchEventModal
			open={shownModal === Modals.SEARCH_EVENT}
			onClose={closeModal}
			onSelected={onEventSelected}
		/>
		<SearchTypeModal
			open={shownModal === Modals.SEARCH_TYPE}
			onClose={closeModal}
			onSelected={onTypeSelected}
			exclude={form.items.map(i => i.type_id)}
		/>
		<Modal open={shownModal === Modals.ADD_TYPE} onClose={closeModal} size='tiny'>
			<Modal.Header>Agregar equipo</Modal.Header>
			<Modal.Content>
				{selectedItem && (
					<>
						<Field label='Tipo'>
							<Input value={selectedItem.type_name} readonly />
						</Field>
						<Input
							label='Cantidad'
							value={itemQuantity}
							onChange={setItemQuantity}
							inputType='number'
						/>
					</>
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={closeModal} />
				<Button color='black' text='Guardar' onClick={onConfirmAddType} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRM_REMOVE_ITEM} onClose={closeModal} size='tiny'>
			<Modal.Header>Eliminar equipo</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar este equipo de la solicitud?' subheaderStyle={{ marginTop: 8, marginBottom: 15 }} />
				{selectedItem && (
					<Table details data={[
						['Tipo', selectedItem.type_name],
						['Cantidad', selectedItem.quantity],
					]} />
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Eliminar' color='red' onClick={confirmRemoveItem} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT_ITEM} onClose={closeModal} size='tiny'>
			<Modal.Header>Editar cantidad</Modal.Header>
			<Modal.Content>
				{selectedItem && (
					<>
						<Field label='Tipo'>
							<Input value={selectedItem.type_name} readonly />
						</Field>
						<Input
							label='Cantidad'
							value={itemQuantity}
							onChange={setItemQuantity}
							inputType='number'
						/>
					</>
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={closeModal} />
				<Button color='black' text='Guardar' onClick={confirmEditItem} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRM_REMOVE_EVENT} onClose={closeModal} size='tiny'>
			<Modal.Header>Eliminar evento</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar este evento de la solicitud?' subheaderStyle={{ marginTop: 8, marginBottom: 15 }} />
				{selectedEvent && (
					<Table details data={[
						['ID', selectedEvent.date_id],
						['Evento', selectedEvent.event_name],
						['Fecha', moment.unix(selectedEvent.date).format('DD/MM/YY')],
					]} />
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Eliminar' color='red' onClick={confirmRemoveEvent} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.APPROVE} onClose={closeModal} size='tiny'>
			<Modal.Header>Aprobar solicitud</Modal.Header>
			<Modal.Content>
				<Header text='¿Aprobar esta solicitud?' subtext='La solicitud será marcada como aprobada.' />
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Guardar' color='black' onClick={onApprove} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.FINALIZE} onClose={closeModal} size='tiny'>
			<Modal.Header>Finalizar solicitud</Modal.Header>
			<Modal.Content>
				<Header text='¿Finalizar esta solicitud?' subtext='La solicitud express será marcada como finalizada.' />
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Guardar' color='black' onClick={onFinalize} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.REJECT} onClose={closeModal} size='tiny'>
			<Modal.Header>Rechazar solicitud</Modal.Header>
			<Modal.Content>
				<Header text='¿Rechazar esta solicitud?' subtext='La solicitud será marcada como rechazada.' />
				<Input
					label='Motivo de rechazo'
					value={rejectionReason}
					onChange={setRejectionReason}
					textarea
				/>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Rechazar' color='red' onClick={onReject} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT_INFO} onClose={closeModal} size='mini'>
			<Modal.Header>Editar información</Modal.Header>
			<Modal.Content>
				{editInfoForm && (
					<>
						{form.is_express ? (
							<Input
								label='Fecha retorno'
								value={editInfoForm.date_return}
								calendar={{ date: editInfoForm.date_return, mode: 'date', format: 'DD/MM/YY' }}
								onChange={onEditInfoChange('date_return')}
								style={{ width: '100%' }}
							/>
						) : (
							<Field amount={2}>
								<Input
									label='Inicio taquilla'
									value={editInfoForm.box_office_start_date}
									calendar={{ date: editInfoForm.box_office_start_date, mode: 'date', format: 'DD/MM/YY' }}
									onChange={onEditInfoChange('box_office_start_date')}
									style={{ width: '100%' }}
								/>
								<Input
									label='Fecha retorno'
									value={editInfoForm.date_return}
									calendar={{ date: editInfoForm.date_return, mode: 'date', format: 'DD/MM/YY' }}
									onChange={onEditInfoChange('date_return')}
									style={{ width: '100%' }}
								/>
							</Field>
						)}
						<Input
							label='Comentarios'
							value={editInfoForm.description}
							onChange={onEditInfoChange('description')}
							textarea
						/>
					</>
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Guardar' color='black' onClick={onEditInfo} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CREATE_SHIPMENT} onClose={closeModal} size='small'>
			<Modal.Header>Crear envío</Modal.Header>
			<Modal.Content>
				<Field label='Tipo de dirección' style={{ marginBottom: 10 }}>
					<Dropdown
						selection
						fluid
						value={locationType}
						onChange={(e, { value })=>{
							setLocationType(value as LocationType);
							setShipmentLocation(null);
							setShipmentLocationQuery('');
							setShipmentDestinationZipcode('');
						}}
						options={[
							{ key: LocationType.ADDRESS, value: LocationType.ADDRESS, text: 'Dirección exacta' },
							{ key: LocationType.ESTABLISHMENT, value: LocationType.ESTABLISHMENT, text: 'Recinto / Lugar público' },
						]}
					/>
				</Field>
				<Field label='Destino'>
					<Autocomplete
						key={locationType}
						value={shipmentLocationQuery}
						onChange={setShipmentLocationQuery}
						onPlaceSelected={onShipmentPlaceSelected}
						types={locationType === LocationType.ESTABLISHMENT ? ['establishment'] : ['address']}
						placeholder={locationType === LocationType.ESTABLISHMENT ? 'Buscar recinto o lugar público...' : 'Buscar dirección de destino...'}
					/>
				</Field>
				<Input
					label='Comentarios'
					value={shipmentDescription}
					onChange={setShipmentDescription}
					textarea
				/>
				<Table
					title='Equipos solicitados'
					striped
					style={{ marginTop: 15 }}
					headers={['', 'Tipo', 'Cantidad', '']}
					collapsingIndexes={[0, 2, 3]}
					fitted
				>
					{form?.items.map(item => {
						var selectedCount = shipmentItems.filter(i => i.type_id === item.type_id).length;
						var met = selectedCount >= item.quantity;
						return (
							<Table.Row
								key={item.type_id}
								data={[
									<Icon name={item.type_icon as any} />,
									item.type_name,
									<span style={{ fontWeight: 'bold', color: met ? 'green' : selectedCount > 0 ? 'orange' : 'inherit' }}>
										{selectedCount} / {item.quantity}
									</span>,
									<Button
										size='tiny'
										iconName='circle-plus'
										icon
										color='black'
										disabled={!shipmentDestinationZipcode || met}
										onClick={() => openAddShipmentType(item.type_id)}
									/>
								]}
							/>
						);
					})}
				</Table>
				<Table
					title='Equipos seleccionados'
					selectable
					fitted
					style={{ marginTop: 0 }}
					collapsingIndexes={[0, 1, 3, 4, 5]}
					centeredIndexes={[0]}
					headers={[
						<i className="checkmark icon" style={{ marginRight: 0 }}></i>,
						'Tipo',
						'Nombre',
						'Ubicación',
						'Distancia',
						''
					]}
					onClick={(vals: any[]) => {
						var item_id = vals[5];
						if(item_id != null) onRemoveShipmentItem(item_id);
					}}
					data={shipmentItems.length !== 0 ? shipmentItems.map(item => [
						<i className='large icon circle check'></i>,
						<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
							<Icon name={item.type_icon as any} />
							<span>{item.type_name}</span>
						</div>,
						item.item_name,
						item.warehouse_name
							? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name='warehouse' /><span>{item.warehouse_name}</span></div>
							: item.location_city
								? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name='location-dot' /><span>{item.location_city}</span></div>
								: <Icon name='minus' />,
						item.distance !== null && item.distance !== undefined ? `${item.distance.toFixed(2)} km` : <Icon name='minus' />,
						item.item_id,
					]) : undefined}
				>
					{shipmentItems.length === 0 && (
						<Table.Cell row className='empty' colSpan={6}>
							{shipmentDestinationZipcode ? 'Agrega equipos al envío' : 'Selecciona un destino primero'}
						</Table.Cell>
					)}
				</Table>
				<Message type='error' list={shipmentPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={closeModal} />
				<Button text='Crear envío' color='black' onClick={onCreateShipment} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.UNAVAILABLE_ITEMS} onClose={closeModal} size='tiny'>
			<Modal.Header>Inventario no disponible</Modal.Header>
			<Modal.Content>
				<Header
					text='No se pudo crear la solicitud'
					subtext='Los siguientes equipos no cuentan con suficiente inventario disponible para las fechas solicitadas:'
					subheaderStyle={{ marginTop: 8, marginBottom: 15 }}
				/>
				<Table
					striped
					fitted
					headers={['', 'Tipo', 'Cantidad']}
					collapsingIndexes={[0, 2]}
					style={{ marginTop: 15 }}
				>
					{form?.items.filter(i => unavailableTypeIds.includes(i.type_id)).map(i => (
						<Table.Row
							key={i.type_id}
							centeredIndexes={[2]}
							data={[
								<Icon name={i.type_icon as any} />,
								i.type_name,
								i.quantity,
							]}
						/>
					))}
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' color='black' onClick={closeModal} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EVENT_CONFLICTS} onClose={closeModal} size='tiny'>
			<Modal.Header>Eventos con conflicto</Modal.Header>
			<Modal.Content>
				<Header
					text='No se pudo guardar la solicitud'
					subtext={modalPrompts?.[0]}
					subheaderStyle={{ marginTop: 8, marginBottom: 15 }}
				/>
				<Table
					striped
					fitted
					headers={['Evento', 'Nombre', 'Fecha', 'Solicitud']}
					collapsingIndexes={[0, 2, 3]}
					style={{ marginTop: 15 }}
				>
					{conflictingEvents.map(e => (
						<Table.Row
							key={e.date_id}
							centeredIndexes={[2, 3]}
							data={[
								e.date_id,
								e.event_name,
								moment.unix(e.date).format('DD/MM/YY'),
								e.request_id ? (
									<Link
										to={`/requests/${e.request_id}`}
										target='_blank'
										rel='noopener noreferrer'
										style={{ padding: 0, textDecoration: 'underline', background: 'lightgray', borderRadius: 9999 }}
									>
										{e.request_id}
									</Link>
								) : <Icon name='minus' />,
							]}
						/>
					))}
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' color='black' onClick={closeModal} />
			</Modal.Actions>
		</Modal>
		<SearchAvailableItemsModal
			open={shownModal === Modals.SEARCH_SHIPMENT_ITEMS}
			onClose={() => setShownModal(Modals.CREATE_SHIPMENT)}
			onSelected={onShipmentItemSelected}
			destination_zipcode={shipmentDestinationZipcode}
			initialTypeId={selectedShipmentTypeId}
			selectedItems={shipmentItems}
			requestedItems={form?.items}
		/>
	</div>;
};

export default RequestView;
